/**
 * API設定管理（最適化版）
 */

import { TIMING } from '@src/constants/timing';
import { getMockModeFromStorage } from '@src/modules/local_storage_utils';
import { getCurrentISOString } from '@src/utils/dateUtils';

/**
 * 環境設定の型定義
 */
interface ApiConfig {
  baseUrl: string;
  isMockMode: boolean;
  timeout: number;
}

/**
 * 現在の環境がモックモードかどうかを判定（最適化版）
 */
export function isMockMode(): boolean {
  // ローカルストレージからモックモードの状態を取得（最優先）
  const storedMockMode = getMockModeFromStorage();

  // ローカルストレージに保存された設定がある場合はそれを使用
  if (storedMockMode !== null) {
    return storedMockMode;
  }

  // URL パラメータでモックモードを制御（後方互換性のため）
  const urlParams = new URLSearchParams(window.location.search);
  const mockParam = urlParams.get('mock');

  // ?mock=true または ?mock=1 でモックモードを有効化
  if (mockParam === 'true' || mockParam === '1') {
    return true;
  }

  return false;
}

/**
 * API設定を取得（最適化版）
 *
 * 注意: VITE_API_URL は必ずプロトコル（http:// または https://）を含めて設定してください。
 * 例: https://api.example.com
 */
export function getApiConfig(): ApiConfig {
  const mockMode = isMockMode();

  // 本番環境でVITE_API_URLが未設定の場合はエラーをスロー
  if (import.meta.env.PROD && !mockMode && !import.meta.env.VITE_API_URL) {
    throw new Error(
      'VITE_API_URLが設定されていません。\n' +
        'Cloudflare Pagesの環境変数設定で VITE_API_URL を設定してください。\n' +
        '注意: 必ずプロトコル（https://）を含めて設定してください。'
    );
  }

  let apiUrl = (import.meta.env.VITE_API_URL || 'http://127.0.0.1:3000').trim();

  // プロトコルが欠落している場合、自動的にhttpsを追加（本番環境のみ）
  // 注意: 本来はプロトコルを含めて設定すべきですが、互換性のため自動追加しています
  if (!mockMode && apiUrl && !apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
    apiUrl = `https://${apiUrl}`;
    console.warn(
      `⚠️ VITE_API_URLにプロトコルが欠落していたため、自動的にhttps://を追加しました: ${apiUrl}\n` +
        '推奨: 環境変数にはプロトコルを含めて設定してください（例: https://api.example.com）'
    );
  }

  // 環境に応じてタイムアウトを設定
  // 製品環境: 30秒、開発環境: 15秒、デフォルト: 30秒
  const timeout = import.meta.env.PROD
    ? 30000
    : import.meta.env.DEV
      ? 15000
      : TIMING.API_REQUEST_TIMEOUT;

  return {
    baseUrl: mockMode
      ? 'http://localhost:4010' // Prismモックサーバー
      : apiUrl, // 実際のAPI
    isMockMode: mockMode,
    timeout,
  };
}

/**
 * モックモード用のAPI応答を生成するヘルパー（最適化版）
 */
export function createMockResponse<T>(
  data: T,
  delay: number = TIMING.MOCK_RESPONSE_DEFAULT_DELAY
): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(data), delay);
  });
}

/**
 * ログイン応答のモックデータ
 */
const _mockLoginResponse = {
  success: true,
  data: {
    accessToken: `mock_access_token_${Date.now()}`,
    refreshToken: `mock_refresh_token_${Date.now()}`,
    expiresIn: 3600,
    user: {
      id: 1,
      userId: 'mock_user_123',
      name: 'テストユーザー',
      email: 'test@example.com',
      role: 'user' as const,
      isActive: true,
      createdAt: '2024-01-01T00:00:00.000Z',
      lastLoginAt: '2024-01-16T12:00:00.000Z',
    },
  },
  timestamp: getCurrentISOString(),
};

/**
 * ユーザー情報のモックデータ
 */
const _mockUserData = {
  id: 1,
  userId: 'mock_user_123',
  name: 'テストユーザー',
  email: 'test@example.com',
  role: 'user' as const,
  isActive: true,
  createdAt: '2024-01-01T00:00:00.000Z',
  lastLoginAt: '2024-01-16T12:00:00.000Z',
};

/**
 * 成功応答のモックデータ
 */
const _mockSuccessResponse = {
  success: true,
  message: '操作が正常に完了しました',
};

/**
 * API設定のキャッシュをクリア
 */
function clearApiConfigCache(): void {
  // 現在はキャッシュを使用していないため、何もしない
  // 将来的にキャッシュを実装する場合は、ここでクリア処理を追加
}

/**
 * モックモード変更時のAPI設定更新
 */
function _updateApiConfigOnMockModeChange(): void {
  clearApiConfigCache();
  // 新しい設定を取得
  getApiConfig();
}
